// Manage Calendars dialog — create / rename / delete shared calendars,
// add / remove members. Members are picked from the all-users list via
// calendar.listShareableUsers (the server omits the current user).
//
// Role handling is minimal in Phase 2A: new members are "editor" by
// default. The owner row is shown read-only and can't be removed
// (server gates this anyway).

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { PALETTE } from "../../lib/calendar-utils";
import { api, type Calendar, type CalendarMember, type CalendarShareableUser, type Household } from "../../lib/rpc";
import type { UseCalendars } from "../../lib/use-calendars";

interface ManageCalendarsProps {
  open: boolean;
  onClose: () => void;
  cals: UseCalendars;
  notify: (msg: string) => void;
}

export function ManageCalendars({ open, onClose, cals, notify }: ManageCalendarsProps) {
  const [selected, setSelected] = useState<Calendar | null>(null);
  const [members, setMembers] = useState<CalendarMember[]>([]);
  const [users, setUsers] = useState<CalendarShareableUser[]>([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>("violet");
  const [renameDraft, setRenameDraft] = useState("");
  const [editingName, setEditingName] = useState(false);
  // Households the user owns — only an owner can flag a calendar as the
  // family calendar for their household, so this is the dropdown's
  // contents on the household-calendar row.
  const [ownedHouseholds, setOwnedHouseholds] = useState<Household[]>([]);

  // Re-seed local state any time the dialog re-opens. Avoids stale
  // selected calendar / member list across opens.
  useEffect(() => {
    if (!open) return;
    setSelected(cals.calendars[0] || null);
    setNewName("");
    setNewColor("violet");
    setEditingName(false);
    cals.listShareableUsers().then(setUsers);
    api.householdsList()
      .then((r) => setOwnedHouseholds(r.households.filter((h) => h.role === "owner")))
      .catch(() => setOwnedHouseholds([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !selected) { setMembers([]); return; }
    let cancelled = false;
    cals.listMembers(selected.id).then((m) => { if (!cancelled) setMembers(m); });
    setRenameDraft(selected.name);
    setEditingName(false);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    const cal = await cals.create({ name, color: newColor });
    if (cal) {
      notify("Calendar created");
      setSelected(cal);
      setNewName("");
    }
  };

  const onRename = async () => {
    if (!selected) return;
    const name = renameDraft.trim();
    if (!name || name === selected.name) { setEditingName(false); return; }
    const updated = await cals.rename(selected.id, name);
    if (updated) {
      setSelected(updated);
      setEditingName(false);
      notify("Calendar renamed");
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: `Delete "${selected.name}"?`,
      message: "Events on this calendar will become orphaned but keep their data. Members lose access.",
      confirmLabel: "Delete calendar",
      danger: true,
    });
    if (!ok) return;
    const done = await cals.remove(selected.id);
    if (done) {
      notify("Calendar deleted");
      setSelected(cals.calendars[0] || null);
    }
  };

  const onAddMember = async (userId: string) => {
    if (!selected) return;
    const done = await cals.addMember(selected.id, userId);
    if (done) {
      const m = await cals.listMembers(selected.id);
      setMembers(m);
      notify(`Added ${userId}`);
    }
  };

  const onRemoveMember = async (userId: string) => {
    if (!selected) return;
    const done = await cals.removeMember(selected.id, userId);
    if (done) {
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
      notify(`Removed ${userId}`);
    }
  };

  const memberIds = new Set(members.map((m) => m.userId));
  const addableUsers = users.filter((u) => !memberIds.has(u.id));
  const canManage = selected?.myRole === "owner";

  return (
    <div className="cal-modal-bg" onClick={onClose}>
      <div className="cal-manage-sheet" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="cal-new-handle" />
        <header className="cal-new-head">
          <div>
            <h3 className="cal-new-title">Calendars</h3>
            <div className="cal-new-sub">Shared schedules and who's on them.</div>
          </div>
          <button className="icon-btn" onClick={onClose} type="button">
            <Icon name="x" />
          </button>
        </header>

        <div className="cal-manage-body">
          <aside className="cal-manage-side">
            <div className="eyebrow" style={{ padding: "8px 12px" }}>Your calendars</div>
            <div className="cal-manage-list">
              {cals.calendars.length === 0 && (
                <div className="cal-manage-empty">No shared calendars yet.</div>
              )}
              {cals.calendars.map((c) => (
                <button
                  key={c.id}
                  className="cal-manage-row"
                  data-active={selected?.id === c.id}
                  onClick={() => setSelected(c)}
                  type="button"
                >
                  <span
                    className="cal-manage-dot"
                    style={{ background: c.color ? `var(--${c.color})` : "var(--accent)" }}
                  />
                  <span style={{ flex: 1, textAlign: "left" }}>{c.name}</span>
                  <span className="cal-manage-role">{c.myRole}</span>
                </button>
              ))}
            </div>

            <div className="cal-manage-create">
              <div className="eyebrow" style={{ marginBottom: 6 }}>New calendar</div>
              <input
                className="input"
                placeholder="Calendar name"
                value={newName}
                onInput={(e) => setNewName((e.currentTarget as HTMLInputElement).value)}
                onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
              />
              <div className="cal-manage-colors">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="cal-manage-color"
                    data-active={newColor === c}
                    style={{ background: `var(--${c})` }}
                    onClick={() => setNewColor(c)}
                    aria-label={c}
                  />
                ))}
              </div>
              <button
                className="btn btn-primary"
                onClick={onCreate}
                disabled={!newName.trim()}
                type="button"
                style={{ marginTop: 8 }}
              >
                <Icon name="plus" size={13} /> Create
              </button>
            </div>
          </aside>

          <div className="cal-manage-main">
            {!selected ? (
              <div className="cal-manage-empty-pane">
                <Icon name="people" size={32} />
                <p>Pick a calendar on the left, or create one.</p>
              </div>
            ) : (
              <>
                <div className="cal-manage-header">
                  {editingName ? (
                    <input
                      className="input cal-manage-rename"
                      autoFocus
                      value={renameDraft}
                      onInput={(e) => setRenameDraft((e.currentTarget as HTMLInputElement).value)}
                      onBlur={onRename}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onRename();
                        if (e.key === "Escape") { setRenameDraft(selected.name); setEditingName(false); }
                      }}
                    />
                  ) : (
                    // Plain heading (not interactive itself) + small pencil
                    // edit button next to the name. Mirrors the pantry-name
                    // pattern — touch users see the pencil and know it's
                    // editable; accidentally tapping the title doesn't drop
                    // them into rename mode.
                    <h4 className="cal-manage-name">
                      <span
                        className="cal-manage-dot"
                        style={{ background: selected.color ? `var(--${selected.color})` : "var(--accent)" }}
                      />
                      <span className="cal-manage-name-text">{selected.name}</span>
                      {canManage && (
                        <button
                          className="cal-manage-name-edit"
                          onClick={() => setEditingName(true)}
                          title="Rename"
                          aria-label="Rename"
                          type="button"
                        >
                          <Icon name="edit" size={14} />
                        </button>
                      )}
                    </h4>
                  )}
                  {canManage && (
                    <button className="btn cal-manage-delete" onClick={onDelete} type="button">
                      <Icon name="trash" size={13} /> Delete
                    </button>
                  )}
                </div>

                {canManage && ownedHouseholds.length > 0 && (
                  <div className="cal-manage-household-row">
                    <div className="eyebrow">Family calendar for</div>
                    <select
                      className="input"
                      value={selected.householdId ?? ""}
                      onChange={async (e) => {
                        const v = (e.currentTarget as HTMLSelectElement).value;
                        const next = v ? Number(v) : null;
                        const updated = await cals.setHousehold(selected.id, next);
                        if (updated) {
                          setSelected(updated);
                          notify(next ? "Marked as family calendar — kids in that household now see events on it" : "Cleared family-calendar flag");
                        }
                      }}
                    >
                      <option value="">Not a family calendar</option>
                      {ownedHouseholds.map((h) => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </select>
                    <div className="cal-manage-household-hint">
                      Kids in the selected household see every event on this calendar.
                    </div>
                  </div>
                )}

                <div className="eyebrow">Members ({members.length})</div>
                <ul className="cal-manage-members">
                  {members.map((m) => (
                    <li key={m.userId} className="cal-manage-member">
                      <span className="cal-manage-avatar">{m.userId.slice(0, 1).toUpperCase()}</span>
                      <span className="cal-manage-uid">{m.userId}</span>
                      <span className="cal-manage-role">{m.role}</span>
                      {canManage && m.role !== "owner" && (
                        <button
                          className="icon-btn"
                          onClick={() => onRemoveMember(m.userId)}
                          title="Remove"
                          type="button"
                        >
                          <Icon name="x" size={12} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {canManage && addableUsers.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 16 }}>Add member</div>
                    <ul className="cal-manage-addable">
                      {addableUsers.map((u) => (
                        <li key={u.id}>
                          <button
                            className="cal-manage-addable-row"
                            onClick={() => onAddMember(u.id)}
                            type="button"
                          >
                            <span className="cal-manage-avatar">{u.id.slice(0, 1).toUpperCase()}</span>
                            <span style={{ flex: 1, textAlign: "left" }}>{u.id}</span>
                            <Icon name="plus" size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
